// Relocation service layer — placeholder data for the Relocation Assistant module.
// Shaped so it can be swapped for real relocation, visa, embassy and cost-of-living APIs later.

export type ChecklistItem = { id: string; label: string; category: string; done?: boolean };

export function getRelocationChecklist(country: string): ChecklistItem[] {
  const c = country || "your destination";
  return [
    { id: "1", label: `Confirm visa requirements for ${c}`, category: "Legal" },
    { id: "2", label: "Renew passport (12+ months validity)", category: "Legal" },
    { id: "3", label: "Book flight & travel insurance", category: "Travel" },
    { id: "4", label: "Secure temporary accommodation (2–4 weeks)", category: "Housing" },
    { id: "5", label: "Notify bank & set up international access", category: "Finance" },
    { id: "6", label: "Transfer or cancel utilities & subscriptions", category: "Finance" },
    { id: "7", label: "Get international driver's permit", category: "Travel" },
    { id: "8", label: "Collect academic & work references", category: "Career" },
    { id: "9", label: "Complete medical exam & vaccinations", category: "Health" },
    { id: "10", label: "Pack essential documents in carry-on", category: "Travel" },
    { id: "11", label: "Set up mail forwarding", category: "Admin" },
    { id: "12", label: `Learn basic ${c} customs & greetings`, category: "Culture" },
  ];
}

export type CountryGuide = {
  country: string;
  language: string;
  currency: string;
  timezone: string;
  climate: string;
  costIndex: number; // 1-100
  highlights: string[];
  cautions: string[];
};

const GUIDES: CountryGuide[] = [
  {
    country: "Germany",
    language: "German",
    currency: "EUR",
    timezone: "CET",
    climate: "Temperate, cold winters",
    costIndex: 72,
    highlights: ["Strong job market", "Excellent public transit", "Universal healthcare"],
    cautions: ["Bureaucracy is heavy", "Rental market is competitive"],
  },
  {
    country: "Canada",
    language: "English / French",
    currency: "CAD",
    timezone: "Multiple",
    climate: "Cold winters, warm summers",
    costIndex: 74,
    highlights: ["Clear PR pathway", "High quality of life", "Multicultural cities"],
    cautions: ["Housing costs in Toronto/Vancouver", "Cold winters in the north"],
  },
  {
    country: "United Arab Emirates",
    language: "Arabic / English",
    currency: "AED",
    timezone: "GST",
    climate: "Hot desert",
    costIndex: 78,
    highlights: ["Tax-free income", "Modern infrastructure", "Golden Visa options"],
    cautions: ["Summer heat 45°C+", "Alcohol licensing rules"],
  },
  {
    country: "Netherlands",
    language: "Dutch / English",
    currency: "EUR",
    timezone: "CET",
    climate: "Mild, rainy",
    costIndex: 76,
    highlights: ["English widely spoken", "30% tax ruling", "Bike-friendly"],
    cautions: ["Housing shortage", "Long waiting lists for rentals"],
  },
  {
    country: "Australia",
    language: "English",
    currency: "AUD",
    timezone: "Multiple",
    climate: "Varied, mostly warm",
    costIndex: 80,
    highlights: ["Skilled migration program", "High wages", "Outdoor lifestyle"],
    cautions: ["Isolation from home", "Housing costs in Sydney"],
  },
];

export function getCountryGuides(): CountryGuide[] {
  return GUIDES;
}
export function getCountryGuide(country: string): CountryGuide | undefined {
  return GUIDES.find((g) => g.country.toLowerCase() === country.toLowerCase());
}

export type MovingCost = {
  flights: number;
  shipping: number;
  visa: number;
  insurance: number;
  deposit: number;
  temporaryStay: number;
  initialLiving: number;
  buffer: number;
  total: number;
};

export function calculateMovingCost(input: {
  people: number;
  destinationCostIndex: number;
  shippingLevel: "carry-on" | "boxes" | "container";
  weeksTemporary: number;
}): MovingCost {
  const idx = Math.max(30, Math.min(100, input.destinationCostIndex || 70)) / 70;
  const flights = 900 * input.people * idx;
  const shipping =
    input.shippingLevel === "container" ? 4200 : input.shippingLevel === "boxes" ? 1200 : 250;
  const visa = 350 * input.people;
  const insurance = 180 * input.people;
  const deposit = 1800 * idx;
  const temporaryStay = 120 * idx * Math.max(1, input.weeksTemporary) * 7;
  const initialLiving = 900 * input.people * idx;
  const subtotal = flights + shipping + visa + insurance + deposit + temporaryStay + initialLiving;
  const buffer = Math.round(subtotal * 0.1);
  return {
    flights: Math.round(flights),
    shipping: Math.round(shipping),
    visa: Math.round(visa),
    insurance: Math.round(insurance),
    deposit: Math.round(deposit),
    temporaryStay: Math.round(temporaryStay),
    initialLiving: Math.round(initialLiving),
    buffer,
    total: Math.round(subtotal + buffer),
  };
}

export type DocumentItem = { id: string; label: string; required: boolean; notes: string };
export function getDocumentChecklist(): DocumentItem[] {
  return [
    {
      id: "d1",
      label: "Passport (12+ months validity)",
      required: true,
      notes: "Carry copies in checked & hand baggage.",
    },
    {
      id: "d2",
      label: "Visa or residence permit",
      required: true,
      notes: "Print approval letter + biometric appointment slip.",
    },
    {
      id: "d3",
      label: "Employment contract / offer letter",
      required: true,
      notes: "Signed original, ideally stamped.",
    },
    {
      id: "d4",
      label: "Academic transcripts & diplomas",
      required: true,
      notes: "Apostille or notarize where required.",
    },
    {
      id: "d5",
      label: "Police clearance certificate",
      required: true,
      notes: "Issued within last 3–6 months.",
    },
    {
      id: "d6",
      label: "Medical certificate & vaccinations",
      required: true,
      notes: "Yellow card for tropical destinations.",
    },
    {
      id: "d7",
      label: "Birth certificate",
      required: false,
      notes: "Needed for dependents / family visas.",
    },
    {
      id: "d8",
      label: "Marriage certificate",
      required: false,
      notes: "Required for spouse visa.",
    },
    {
      id: "d9",
      label: "Driver's license + IDP",
      required: false,
      notes: "International Driving Permit valid 1 year.",
    },
    {
      id: "d10",
      label: "Bank statements (last 3–6 months)",
      required: false,
      notes: "Often requested for visa proof of funds.",
    },
  ];
}

export type VisaMilestone = { week: number; label: string; description: string };
export function getVisaTimeline(visaType: string): VisaMilestone[] {
  const v = visaType || "Skilled Worker";
  return [
    {
      week: 0,
      label: `Research ${v} requirements`,
      description: "Confirm eligibility, gather country-specific rules.",
    },
    {
      week: 2,
      label: "Collect documents",
      description: "Passport, degree, police clearance, medical.",
    },
    {
      week: 4,
      label: "Secure sponsor / offer",
      description: "Employer files Certificate of Sponsorship if applicable.",
    },
    {
      week: 6,
      label: "Submit application",
      description: "Online portal + fee payment + biometric booking.",
    },
    {
      week: 8,
      label: "Biometrics appointment",
      description: "Attend visa application centre in your country.",
    },
    {
      week: 12,
      label: "Decision expected",
      description: "Track status; standard processing 4–8 weeks after biometrics.",
    },
    {
      week: 14,
      label: "Passport & vignette collected",
      description: "Book travel once visa is in hand.",
    },
    {
      week: 16,
      label: "Depart & activate",
      description: "Enter country within visa validity window.",
    },
  ];
}

export type Embassy = {
  country: string;
  capital: string;
  address: string;
  phone: string;
  email: string;
  hours: string;
};
const EMBASSIES: Embassy[] = [
  {
    country: "Germany",
    capital: "Berlin",
    address: "Wilhelmstraße 92, 10117 Berlin",
    phone: "+49 30 000 0000",
    email: "info@embassy.example",
    hours: "Mon–Fri 09:00–13:00",
  },
  {
    country: "Canada",
    capital: "Ottawa",
    address: "50 O'Connor St, Ottawa, ON K1P 6L2",
    phone: "+1 613 000 0000",
    email: "info@embassy.example",
    hours: "Mon–Fri 08:30–16:30",
  },
  {
    country: "United Arab Emirates",
    capital: "Abu Dhabi",
    address: "Embassies District, Abu Dhabi",
    phone: "+971 2 000 0000",
    email: "info@embassy.example",
    hours: "Sun–Thu 09:00–14:00",
  },
  {
    country: "Netherlands",
    capital: "The Hague",
    address: "Lange Voorhout 102, 2514 EJ The Hague",
    phone: "+31 70 000 0000",
    email: "info@embassy.example",
    hours: "Mon–Fri 09:00–12:30",
  },
  {
    country: "Australia",
    capital: "Canberra",
    address: "Commonwealth Ave, Canberra ACT 2600",
    phone: "+61 2 0000 0000",
    email: "info@embassy.example",
    hours: "Mon–Fri 09:00–17:00",
  },
];
export function getEmbassies(): Embassy[] {
  return EMBASSIES;
}

export type Reminder = { days: number; label: string };
export function getPreDepartureReminders(): Reminder[] {
  return [
    { days: -30, label: "Confirm visa, flights and travel insurance" },
    { days: -21, label: "Book temporary accommodation for arrival" },
    { days: -14, label: "Notify bank of overseas relocation" },
    { days: -10, label: "Arrange currency exchange / multi-currency card" },
    { days: -7, label: "Print all documents; store digital copies in vault" },
    { days: -5, label: "Cancel/transfer utilities and subscriptions" },
    { days: -3, label: "Confirm airport transfer at destination" },
    { days: -1, label: "Pack carry-on: passport, meds, chargers, cash" },
    { days: 0, label: "Travel day — arrive at airport 3 hours early" },
  ];
}

export type ArrivalStep = { day: string; title: string; tasks: string[] };
export function getFirst30Days(): ArrivalStep[] {
  return [
    {
      day: "Day 1–3",
      title: "Land safely",
      tasks: [
        "Buy local SIM at airport",
        "Withdraw local currency",
        "Check into temporary stay",
        "Sleep off jet lag",
      ],
    },
    {
      day: "Day 4–7",
      title: "Register locally",
      tasks: [
        "Register your address with local authority",
        "Apply for tax/social ID",
        "Open a local bank account",
        "Meet HR & sign onboarding paperwork",
      ],
    },
    {
      day: "Week 2",
      title: "Set up daily life",
      tasks: [
        "Find permanent housing",
        "Get a public transit card",
        "Register with a local doctor",
        "Buy a local mobile plan",
      ],
    },
    {
      day: "Week 3",
      title: "Build a routine",
      tasks: [
        "Explore your neighbourhood",
        "Find grocery + pharmacy",
        "Join one community/meetup",
        "Set up utilities and internet",
      ],
    },
    {
      day: "Week 4",
      title: "Feel at home",
      tasks: [
        "Attend a local event",
        "Start language lessons if needed",
        "Send updated address to bank/employer",
        "Reflect and plan next 60 days",
      ],
    },
  ];
}

export type Accommodation = { type: string; typicalCost: string; goodFor: string };
export function getAccommodationOptions(): Accommodation[] {
  return [
    {
      type: "Airbnb / short-let",
      typicalCost: "$60–150 / night",
      goodFor: "First 1–4 weeks while searching",
    },
    {
      type: "Serviced apartment",
      typicalCost: "$1,500–3,500 / month",
      goodFor: "Families needing furniture + utilities included",
    },
    {
      type: "Shared flat / HMO",
      typicalCost: "$500–900 / month / room",
      goodFor: "Singles on a budget, quick to secure",
    },
    {
      type: "Long-term rental",
      typicalCost: "$900–2,500 / month",
      goodFor: "Once you have local ID + bank account",
    },
    {
      type: "Employer housing",
      typicalCost: "Often subsidized",
      goodFor: "Sponsored roles in remote or high-cost locations",
    },
  ];
}

export type TransportOption = { city: string; primary: string; cardName: string; tips: string[] };
export function getTransportGuides(): TransportOption[] {
  return [
    {
      city: "Berlin",
      primary: "U-Bahn + S-Bahn + tram",
      cardName: "BVG monthly (~€86)",
      tips: ["Buy tickets at machines, validate before boarding", "Bike lanes everywhere"],
    },
    {
      city: "Toronto",
      primary: "TTC subway + streetcars",
      cardName: "PRESTO card",
      tips: ["Free 2-hour transfers on PRESTO", "Uber & Lyft available"],
    },
    {
      city: "Dubai",
      primary: "Metro Red/Green + taxis",
      cardName: "Nol Card",
      tips: ["Ladies-only carriage available", "Fridays are the quiet day"],
    },
    {
      city: "Amsterdam",
      primary: "Tram + metro + bike",
      cardName: "OV-chipkaart",
      tips: ["Bikes cost ~€100 second-hand", "Check-in and check-out at gates"],
    },
    {
      city: "Sydney",
      primary: "Trains + ferries + buses",
      cardName: "Opal card",
      tips: ["Weekly cap on Opal fares", "Ferries are scenic and cheap"],
    },
  ];
}

export type EmergencyContact = {
  country: string;
  police: string;
  ambulance: string;
  fire: string;
  expatHelpline: string;
};
export function getEmergencyContacts(): EmergencyContact[] {
  return [
    {
      country: "Germany",
      police: "110",
      ambulance: "112",
      fire: "112",
      expatHelpline: "+49 30 000 0000",
    },
    {
      country: "Canada",
      police: "911",
      ambulance: "911",
      fire: "911",
      expatHelpline: "+1 613 000 0000",
    },
    {
      country: "United Arab Emirates",
      police: "999",
      ambulance: "998",
      fire: "997",
      expatHelpline: "+971 800 0000",
    },
    {
      country: "Netherlands",
      police: "112",
      ambulance: "112",
      fire: "112",
      expatHelpline: "+31 70 000 0000",
    },
    {
      country: "Australia",
      police: "000",
      ambulance: "000",
      fire: "000",
      expatHelpline: "+61 2 0000 0000",
    },
  ];
}

/* ---------- AI: Profile improvement suggestions (placeholder) ---------- */
export async function suggestProfileImprovements(
  profile: string,
): Promise<{ score: number; suggestions: string[] }> {
  await new Promise((r) => setTimeout(r, 700));
  const base = Math.min(95, 50 + (profile.length % 40));
  return {
    score: base,
    suggestions: [
      "Add a concise 2-line headline that names your role, industry and years of experience.",
      "Quantify at least 3 achievements with numbers (%, $, users, time saved).",
      "List target markets (countries/cities) so employers know your mobility.",
      "Add 5–8 hard skills at the top; recruiters scan for keywords in 6 seconds.",
      "Upload a professional photo and a 30-second intro video to your portfolio.",
      "Request 2 written recommendations from managers or clients.",
    ],
  };
}
